import { useEffect } from 'react';

import type {
    RuntimeMode,
    TradeExecutionPhase,
    TradeExecutionStatus,
} from '@/features/wallet';

import { useTradingStore } from '../store';
import type { PositionType } from '../types';

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

/** the fields of one market's execution state that the store keeps. */
interface ExecutionSlotInput {
    coin: string | null;
    status: TradeExecutionStatus;
    executionPhase: TradeExecutionPhase | null;
    tradeHash: string | null;
    openTxHash: string | null;
    positionType: PositionType | null;
    positionAmount: number | null;
    entryPrice: number | null;
    leverage: number | null;
    /** Price a GTC limit entry rests (or rested) at; null on a market open. */
    limitPrice: string | null;
    /** Real open time (ms epoch); the trade visual reads it verbatim. */
    openedAt: number | null;
    /** Measured request→fill latency of a live open; null when restored. */
    fillMs: number | null;
    /** `live` = opened in this session, `restored` = hydrated from the API. */
    openSource: 'live' | 'restored' | null;
}

interface TradingStoreSyncDeps {
    // Auth (changes rarely — login / mode switch)
    runtimeMode: RuntimeMode;
    token: string | null;
    address: string | undefined;

    // The market the chart is on. The store projects the selected market's slot
    // onto the flat `exec*` fields, so this is what makes the rail follow the
    // chart (MULTI_POSITION_PLAN.md §0, Option A).
    selectedCoin: string | null;

    // Execution (changes during trade lifecycle) — one entry per market the
    // execution hook is driving, keyed by upper-cased coin.
    executionSlots: Record<string, ExecutionSlotInput>;

    // Balance (changes after trades or deposits)
    balance: number;
    balanceForInputCap: number;
    custodialAvailable: number;

    // Custodial balance (changes after deposits/withdrawals)
    custodialNumericBalance: number | null;
    isCustodialBalanceLoading: boolean;
    isCustodialBalanceProvisional: boolean;
    isCustodialBalanceSettling: boolean;

    // Flags (changes rarely)
    hasPairData: boolean;
    /** a Snappy Wallet reactivation is running — blocks every trade entry point. */
    isReactivating: boolean;

    // High-frequency (animation frame updates from price tracking)
    currentValue: number;
    /** the market `currentValue` belongs to — null while it is still the
     *  previous market's, which is a price no position may be measured against. */
    currentValueCoin: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Pushes external hook outputs into `useTradingStore` via batched `useEffect`
 * calls grouped by change-frequency. Called once in Trading.tsx.
 *
 * Each sync action has internal Object.is guards so redundant writes are
 * no-ops — the grouping here just keeps the effect dependency arrays small.
 */
export function useTradingStoreSync(deps: TradingStoreSyncDeps): void {
    // ── Auth group ───────────────────────────────────────────────────────
    useEffect(() => {
        useTradingStore.getState()._syncAuth({
            runtimeMode: deps.runtimeMode,
            token: deps.token,
            address: deps.address,
        });
    }, [deps.runtimeMode, deps.token, deps.address]);

    // ── Selected market ──────────────────────────────────────────────────
    // Runs before the execution group below in source order, but React fires
    // effects in order on every commit, so a first render that already has both
    // still lands the coin before the execution state that depends on it.
    useEffect(() => {
        useTradingStore.getState()._syncSelectedCoin(deps.selectedCoin);
    }, [deps.selectedCoin]);

    // ── Execution group ──────────────────────────────────────────────────
    // One _syncExecution per market being driven. Each has its own Object.is
    // guard in the store, so a map with an unchanged entry costs a comparison
    // and no write — which is what lets this depend on the map identity alone.
    useEffect(() => {
        const store = useTradingStore.getState();
        for (const [coinKey, slot] of Object.entries(deps.executionSlots)) {
            store._syncExecution({
                coin: slot.coin ?? coinKey,
                status: slot.status,
                phase: slot.executionPhase,
                tradeHash: slot.tradeHash,
                openTxHash: slot.openTxHash,
                positionType: slot.positionType,
                positionAmount: slot.positionAmount,
                entryPrice: slot.entryPrice,
                leverage: slot.leverage,
                limitPrice: slot.limitPrice,
                openedAt: slot.openedAt,
                fillMs: slot.fillMs,
                openSource: slot.openSource,
            });
        }
    }, [deps.executionSlots]);

    // ── Balance group ────────────────────────────────────────────────────
    useEffect(() => {
        useTradingStore.getState()._syncBalance({
            balance: deps.balance,
            balanceForInputCap: deps.balanceForInputCap,
            custodialAvailable: deps.custodialAvailable,
        });
    }, [deps.balance, deps.balanceForInputCap, deps.custodialAvailable]);

    // ── Custodial balance group ──────────────────────────────────────────
    useEffect(() => {
        useTradingStore.getState()._syncCustodialBalance({
            numericBalance: deps.custodialNumericBalance,
            isLoading: deps.isCustodialBalanceLoading,
            isSettling: deps.isCustodialBalanceSettling,
            isProvisional: deps.isCustodialBalanceProvisional,
        });
    }, [
        deps.custodialNumericBalance,
        deps.isCustodialBalanceLoading,
        deps.isCustodialBalanceSettling,
        deps.isCustodialBalanceProvisional,
    ]);

    // ── Flags group ──────────────────────────────────────────────────────
    useEffect(() => {
        useTradingStore.getState()._syncHasPairData(deps.hasPairData);
    }, [deps.hasPairData]);

    useEffect(() => {
        useTradingStore.getState()._syncIsReactivating(deps.isReactivating);
    }, [deps.isReactivating]);

    // ── Current value (high-frequency, own effect) ───────────────────────
    useEffect(() => {
        useTradingStore
            .getState()
            .setCurrentValue(deps.currentValue, deps.currentValueCoin);
        // the tag is a dependency too: the price can be numerically unchanged
        // across a market switch (a stale spring value) while the market it
        // belongs to has changed, and that transition is exactly what the tag
        // exists to publish.
    }, [deps.currentValue, deps.currentValueCoin]);
}
